import { Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TFunction } from 'i18next'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { RowActions } from '@/shared/ui/table/RowActions'
import { ACTIVE_FILTER, ACTIVE_ONLY, SHOW_ALL, type Region } from './geoApi'

interface GeoColumnsOptions<T extends Region> {
  t: TFunction
  /** Nomdan keyin qo'yiladigan ustunlar (tumanda — viloyat) */
  extra?: ColumnsType<T>
  onEdit: (row: T) => void
  onToggleActive: (row: T) => void
  pendingId: string | null
}

export function geoColumns<T extends Region>({
  t,
  extra = [],
  onEdit,
  onToggleActive,
  pendingId,
}: GeoColumnsOptions<T>): ColumnsType<T> {
  return [
    {
      key: 'name',
      title: t('geo.name'),
      width: 220,
      sorter: true,
      render: (_, row) => <Typography.Text strong>{row.name}</Typography.Text>,
    },
    ...extra,
    {
      key: 'code',
      title: t('geo.code'),
      width: 120,
      render: (_, row) => row.code ?? t('common.dash'),
    },
    {
      key: ACTIVE_FILTER,
      title: t('geo.status'),
      width: 130,
      /*
        ⚠️ Ro'yxat `GeoFilterBar` dagi tanlov bilan AYNAN bir xil
        bo'lishi shart. `DataTable.handleChange` filtrlarni antd bergan
        ro'yxatdan qayta quradi va antd faqat shu yerda e'lon qilingan
        qiymatlarni biladi — «Barcha holatlar» bu yerda bo'lmasa, u
        tanlangan holda sahifa almashtirilishi bilan jimgina sukutga
        (faqat faollar) qaytardi.
      */
      filters: [
        { text: t('geo.activeOnly'), value: ACTIVE_ONLY },
        { text: t('dataTable.stateAll'), value: SHOW_ALL },
      ],
      filterMultiple: false,
      render: (_, row) => (
        <Tag color={row.active ? 'green' : 'default'}>
          {t(row.active ? 'geo.active' : 'geo.inactive')}
        </Tag>
      ),
    },
    {
      key: 'created',
      title: t('common.createdAt'),
      width: 180,
      sorter: true,
      defaultSortOrder: 'descend',
      render: (_, row) =>
        row.createdAt === null ? (
          t('common.dash')
        ) : (
          <Tooltip title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Tooltip>
        ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('common.edit'),
              permission: 'geo.write',
              onClick: () => onEdit(row),
            },
            {
              key: 'toggle',
              label: t(row.active ? 'geo.deactivate' : 'geo.activate'),
              permission: 'geo.write',
              danger: row.active,
              loading: pendingId === row.id,
              onClick: () => onToggleActive(row),
            },
          ]}
        />
      ),
    },
  ]
}
